import { iframeRequestSystemEnabled, startListener, stopListener } from '@/utils/iframeUtility';
import { localStorageRead, sessionStorageRead } from '@/services/iframe';
import { createApp } from 'vue';
import App from './App.vue';
import router from './router/router';
import ApiService from './services/api';
import { TokenService } from './services/token';
import VueApexCharts from 'vue3-apexcharts';
import ErrorLogService from './services/errorLogService';
import i18n from './i18n';
import '@/sass/main.css';
import { createPinia } from 'pinia';
import vuexPersistCompat from '@/stores/plugins/vuexPersistCompat';
import { registerAllLegacyStores } from '@/stores';
import { initSentry, bindSentryContext } from '@/sentry';

/** True only for a string that parses to a non-null JSON object (the shape vuex-persist expects) */
const isValidVuexPersistenceValue = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;

  try {
    const parsed = JSON.parse(value);
    return parsed !== null && typeof parsed === 'object';
  } catch {
    return false;
  }
};

const syncVuexStorageFromParent = async () => {
  const storageItems = [
    {
      storage: window.localStorage,
      read: localStorageRead,
    },
    {
      storage: window.sessionStorage,
      read: sessionStorageRead,
    },
  ];

  await Promise.all(storageItems.map(async ({ storage, read }) => {
    const parentVuex = await read('vuex');

    if (isValidVuexPersistenceValue(parentVuex)) {
      storage.setItem('vuex', parentVuex);
      return;
    }

    if (!isValidVuexPersistenceValue(storage.getItem('vuex'))) {
      storage.removeItem('vuex');
    }
  }));
};

/**
 * Attach Window.addEventListener for messages, so that we can start
 * asking the parent context for things. The TokenService will use
 * this during init to ask for the token from parent context if we
 * don't already have it.
 */
startListener();
await TokenService.init().catch(console.error);

// Seed the persisted `vuex` storage item from the parent frame BEFORE any Pinia store
// hydrates. Pinia stores hydrate lazily on first use via the vuexPersistCompat plugin, which
// reads this same single `vuex` localStorage/sessionStorage item — so the structure is
// unchanged and existing user drafts are preserved. (The iframe postMessage responder is
// attached later, after Pinia + the legacy bridge are ready.)
if (iframeRequestSystemEnabled) await syncVuexStorageFromParent();

// If token exists set header for api requests
if (TokenService.getToken()) {
  ApiService.setHeader()
  ApiService.mount401Interceptor()
}

const app = createApp(App);

app.component('draggable', {
  // Props, data, methods, etc.
  render() {
    return this.$slots.default!({})
  },
})
app.component('dropzone', {
  // Props, data, methods, etc.
  render() {
    return this.$slots.default!({})
  },
})

// err/instance left as `any`: Vue types err as unknown and we read .message/.stack/.$el freely
app.config.errorHandler = (err: any, instance: any, info: string) => {
  // err: the error object
  // instance: the component instance that threw the error
  // info: a string specifying where the error was captured (e.g., "created hook")

  console.error("Error Caught:", err);
  if (!window.location.href.includes('/livechat')) return; // don't log errors if not from livechat
  // Log error to the server
  ErrorLogService.sendErrorToServer({
    url: window.location.href,
    timestamp: new Date().toISOString(),
    error: {
      component: {
        element: instance?.$el?.className || 'unknown',
      },
      error: {
        message: err.message,
        stack: err.stack,
      },
      info: info,
    },
    userAgent: navigator.userAgent,
  });
};

// Sentry wraps the errorHandler assigned above (see src/sentry.ts for the ordering constraints).
initSentry(app, router);

// Set the base URL of the API https://cli.vuejs.org/guide/mode-and-env.html#modes How to use env files.
ApiService.init(import.meta.env.VITE_BOTCAST_API)

app.use(VueApexCharts)

// register hub components
import HubDialog from '@/components/hub/HubDialog.vue'
app.component('hub-dialog', HubDialog)

// Pinia with the vuex-compat persistence plugin. The plugin reads/writes the single `vuex`
// item in localStorage and sessionStorage exactly as vuex-persist did (same keys, same
// structure, per-store subset support), so persisted caches and user drafts carry over untouched.
const pinia = createPinia();
pinia.use(vuexPersistCompat);
app.use(pinia);

// Register every legacy vuex namespace with the compatibility bridge so incoming
// `vuexStore:mutation:write` postMessages from the old hub resolve to the equivalent Pinia
// store actions (honouring collision renames). Must run before iframeAPI is attached.
registerAllLegacyStores();

// Enrich Sentry events with the logged-in user and the selected bot / channel (needs Pinia).
bindSentryContext(pinia);

/**
 * Dev server only: sign in automatically with the credentials from `.env.development.local`,
 * before the router resolves its first navigation — otherwise the route guard would see a
 * logged-out user and redirect to `/login`. The dynamic import inside the `DEV` branch keeps
 * the module out of production bundles entirely (see src/dev/devAutoLogin.ts).
 */
if (import.meta.env.DEV) {
  const { startDevSession } = await import('@/dev/devAutoLogin');
  await startDevSession();
}

app.use(router)
  .use(i18n);

/**
 * Wait for the router's initial navigation before mounting.
 *
 * vue-router 4 resolves the first navigation asynchronously, so without this the root
 * component mounts against START_LOCATION — `route.meta` is `{}`, and every `meta.public`
 * check reads as `undefined`. vue-router 3 resolved it synchronously, so `this.$route.meta`
 * was already populated in the old hub's `created()`.
 */
await router.isReady();

app.mount('#app');

// Attach the parent<->iframe postMessage responder AFTER Pinia is active and the legacy bridge
// is registered, so an incoming legacy mutation can resolve and dispatch to its target store.
if (iframeRequestSystemEnabled) await import('./utils/iframeAPI');

app.onUnmount(stopListener);
